import React, { useState, useEffect, useCallback, useRef } from 'react';
import api, { getNumber, getBalance, requestPacCode } from '../../api';
import { usePorts } from './hooks/usePorts';
import { usePortForm } from './hooks/usePortForm';
import { usePortSearch } from './hooks/usePortSearch';
import { useGateways } from './hooks/useGateways';
import { usePortActivation } from './hooks/usePortActivation';
import { exportPortsToCSV } from './utils/exportUtils';
import { renderGatewayName } from './utils/portHelpers';
import { usePortActivationStatus, getEffectiveActivationStatus } from '../../shared/portActivation';
import PortForm from './components/PortForm';
import PortTable from './components/PortTable';
import PortSearch from './components/PortSearch';
import SimLifecycleBanner from './components/SimLifecycleBanner';
import PaginationControls from './components/PaginationControls';

/**
 * Main Port Management Component
 * Refactored from the original 925-line file into modular components and hooks
 */
const SYNC_BTN_STYLE = {
  fontWeight: 700,
  fontSize: '13px',
  padding: '0.35rem 0.85rem',
  borderRadius: '6px',
  border: '2px solid #e65c00',
  background: 'linear-gradient(135deg,#ff8c00,#e65c00)',
  color: '#fff',
  boxShadow: '0 2px 6px rgba(230,92,0,0.4)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

function PortManagement() {
  const readOnly = localStorage.getItem('staff_role') === 'FF Staff';
  const crmUserId = Number(localStorage.getItem('crm_user_id'));
  // Same allow-list as backend Check Number / Get balance / Get Pack Code endpoints
  const canGetNumber = crmUserId === 43 || crmUserId === 55;

  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [numberAlert, setNumberAlert] = useState(null);
  const [balanceAlert, setBalanceAlert] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [gettingNumberPortId, setGettingNumberPortId] = useState(null);
  const [gettingBalancePortId, setGettingBalancePortId] = useState(null);
  const [gettingPacCodePortId, setGettingPacCodePortId] = useState(null);
  const syncingRef = useRef(false);

  // Port data fetching (must come first as it's used by other hooks)
  const {
    ports,
    setPorts,
    loading,
    error: portsError,
    debouncedFetch,
    fetchPorts,
    showArchived,
    setShowArchived,
  } = usePorts();

  // Update error state from ports hook
  useEffect(() => {
    if (portsError) {
      setError(portsError);
    }
  }, [portsError]);

  // Port form management
  const {
    formData,
    editingPort,
    setFormData,
    handleInputChange,
    handleSubmit,
    handleEdit,
    handleDelete,
    handleArchive,
    resetForm,
  } = usePortForm(ports, setPorts, setError, setSuccess);

  // Websocket subscription for port updates
  useEffect(() => {
    const unsubscribe = api.subscribeTo('port_update', (payload) => {
      console.log('Port update received:', payload);

      if (payload.action === 'delete') {
        // Refresh ports list
        fetchPorts(showArchived);
      } else if (editingPort?.id === payload.id) {
        setError('This port was just updated by another user. Please refresh.');
        resetForm();
        debouncedFetch();
      } else {
        setPorts((prevPorts) =>
          prevPorts.map((port) =>
            port.id === payload.id ? { ...port, ...payload.data } : port
          )
        );
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingPort?.id, debouncedFetch, resetForm, setPorts, fetchPorts, showArchived]);

  // Gateway management
  const { gateways } = useGateways(setFormData);

  // Port search, filtering, and pagination
  const {
    searchTerm,
    selectedGatewayId,
    selectedSourceFilter,
    selectedMobileOperator,
    mobileOperatorOptions,
    UNKNOWN_OPERATOR,
    sortByOldestMsg,
    setSortByOldestMsg,
    lifecycleFilter,
    lifecycleCounts,
    toggleLifecycleFilter,
    clearLifecycleFilter,
    currentPage,
    setCurrentPage,
    paginatedPorts,
    totalPages,
    sortedPorts,
    handleSearchChange,
    clearSearch,
    showUnassigned,
    handleGatewayFilterChange,
    clearGatewayFilter,
    handleSourceFilterChange,
    clearSourceFilter,
    selectedConversionFilter,
    handleConversionFilterChange,
    clearConversionFilter,
    NO_BOX_FILTER,
    handleMobileOperatorFilterChange,
    clearMobileOperatorFilter,
    ITEMS_PER_PAGE,
  } = usePortSearch(ports);

  // Port activation status (hardware-first, polled + websocket)
  const {
    portStatus,
    refresh: refreshActivationStatus,
  } = usePortActivationStatus({ scope: 'staff' });

  // Port activation actions
  const { handleActivate } = usePortActivation(setError, setSuccess);

  const handleGetNumber = useCallback(
    async (port) => {
      if (
        !port?.id ||
        gettingNumberPortId != null ||
        gettingBalancePortId != null ||
        gettingPacCodePortId != null
      )
        return;

      const st = getEffectiveActivationStatus(portStatus[port.id] || {});
      if (st !== 'active') {
        setError(
          `Port ${port.port_number || port.id} must be active before checking number (current: ${st}).`
        );
        setSuccess(null);
        return;
      }

      setGettingNumberPortId(port.id);
      setError(null);
      try {
        const data = await getNumber(port.id);
        const number = data?.number;
        if (number) {
          setNumberAlert({
            number,
            portNumber: port.port_number,
            gatewayName: renderGatewayName(port, gateways),
          });
          setSuccess(null);
        } else {
          setError('Could not get a phone number');
        }
      } catch (err) {
        const detail =
          err?.response?.data?.detail ||
          err?.message ||
          'Failed to get number';
        setError(typeof detail === 'string' ? detail.replace(/\s*via USSD/gi, '').replace(/\bUSSD\b/g, 'Request') : JSON.stringify(detail));
        setSuccess(null);
      } finally {
        setGettingNumberPortId(null);
      }
    },
    [gettingNumberPortId, gettingBalancePortId, gettingPacCodePortId, portStatus, gateways]
  );

  const handleGetBalance = useCallback(
    async (port) => {
      if (
        !port?.id ||
        gettingNumberPortId != null ||
        gettingBalancePortId != null ||
        gettingPacCodePortId != null
      )
        return;

      const st = getEffectiveActivationStatus(portStatus[port.id] || {});
      if (st !== 'active') {
        setError(
          `Port ${port.port_number || port.id} must be active before getting balance (current: ${st}).`
        );
        setSuccess(null);
        return;
      }

      setGettingBalancePortId(port.id);
      setError(null);
      try {
        const data = await getBalance(port.id);
        const balance = data?.balance;
        if (balance) {
          setBalanceAlert({
            balance,
            portNumber: port.port_number,
            gatewayName: renderGatewayName(port, gateways),
          });
          setSuccess(null);
        } else {
          setError('Could not get balance');
        }
      } catch (err) {
        const detail =
          err?.response?.data?.detail ||
          err?.message ||
          'Failed to get balance';
        setError(typeof detail === 'string' ? detail.replace(/\s*via USSD/gi, '').replace(/\bUSSD\b/g, 'Request') : JSON.stringify(detail));
        setSuccess(null);
      } finally {
        setGettingBalancePortId(null);
      }
    },
    [gettingNumberPortId, gettingBalancePortId, gettingPacCodePortId, portStatus, gateways]
  );

  const handleRequestPacCode = useCallback(
    async (port) => {
      if (
        !port?.id ||
        gettingNumberPortId != null ||
        gettingBalancePortId != null ||
        gettingPacCodePortId != null
      )
        return;

      const st = getEffectiveActivationStatus(portStatus[port.id] || {});
      if (st !== 'active') {
        setError(
          `Port ${port.port_number || port.id} must be active before requesting PAC code (current: ${st}).`
        );
        setSuccess(null);
        return;
      }

      setGettingPacCodePortId(port.id);
      setError(null);
      try {
        await requestPacCode(port.id);
        setSuccess(
          `PAC request sent from port ${port.port_number || port.id}. Watch for the reply SMS.`
        );
        setPorts((prev) =>
          (prev || []).map((p) =>
            p.id === port.id
              ? {
                  ...p,
                  sim_pac_code_requested_at: new Date().toISOString(),
                  last_message_sent: new Date().toISOString(),
                }
              : p
          )
        );
      } catch (err) {
        const detail =
          err?.response?.data?.detail ||
          err?.message ||
          'Failed to request PAC code';
        setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
        setSuccess(null);
      } finally {
        setGettingPacCodePortId(null);
      }
    },
    [
      gettingNumberPortId,
      gettingBalancePortId,
      gettingPacCodePortId,
      portStatus,
      setPorts,
    ]
  );

  const handleSyncFromHardware = useCallback(async () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    setSyncing(true);
    try {
      await refreshActivationStatus();
      setLastSyncTime(new Date());
    } catch {
      // non-fatal
    } finally {
      setSyncing(false);
      syncingRef.current = false;
    }
  }, [refreshActivationStatus]);

  // Auto-sync on mount
  useEffect(() => {
    handleSyncFromHardware();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // CSV Export
  const handleExportCSV = useCallback(() => {
    exportPortsToCSV(sortedPorts, (port) => renderGatewayName(port, gateways));
  }, [sortedPorts, gateways]);

  // Auto-clear flash messages (regular success/error)
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Number / balance alerts stay ~60s; can also be dismissed manually
  useEffect(() => {
    if (!numberAlert) return undefined;
    const timer = setTimeout(() => setNumberAlert(null), 60000);
    return () => clearTimeout(timer);
  }, [numberAlert]);

  useEffect(() => {
    if (!balanceAlert) return undefined;
    const timer = setTimeout(() => setBalanceAlert(null), 60000);
    return () => clearTimeout(timer);
  }, [balanceAlert]);

  return (
    <div className="container-fluid px-3 py-2" style={{ fontSize: '13px' }}>
      {/* Heading */}
      <h4 className="mb-3" style={{ fontSize: '18px' }}>
        {readOnly ? 'Ports' : (editingPort ? 'Edit Port' : 'Add New Port')}
      </h4>

      {/* Flash messages */}
      {error && (
        <div className="alert alert-danger py-2">{error}</div>
      )}
      {success && (
        <div className="alert alert-success py-2">{success}</div>
      )}
      {numberAlert && (
        <div
          className="alert alert-success py-2 d-flex align-items-center justify-content-between gap-2"
          role="alert"
        >
          <div>
            <strong style={{ fontSize: '1.15em', letterSpacing: '0.02em' }}>
              {numberAlert.number}
            </strong>
            <span className="ms-2 text-muted">
              {[numberAlert.portNumber, numberAlert.gatewayName]
                .filter((v) => v && v !== '-')
                .join(' · ')}
            </span>
          </div>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setNumberAlert(null)}
          />
        </div>
      )}
      {balanceAlert && (
        <div
          className="alert alert-success py-2 d-flex align-items-center justify-content-between gap-2"
          role="alert"
        >
          <div>
            <span className="text-muted me-2">Balance</span>
            <strong style={{ fontSize: '1.15em', letterSpacing: '0.02em' }}>
              {balanceAlert.balance}
            </strong>
            <span className="ms-2 text-muted">
              {[balanceAlert.portNumber, balanceAlert.gatewayName]
                .filter((v) => v && v !== '-')
                .join(' · ')}
            </span>
          </div>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setBalanceAlert(null)}
          />
        </div>
      )}

      {/* Add / Edit form — hidden for read-only roles */}
      {!readOnly && (
        <PortForm
          formData={formData}
          editingPort={editingPort}
          gateways={gateways}
          handleInputChange={handleInputChange}
          handleSubmit={handleSubmit}
          resetForm={resetForm}
        />
      )}

      {/* Port List Header Bar */}
      <div className="d-flex flex-wrap justify-content-between align-items-start mb-2 gap-2">
        <h4 className="mb-0" style={{ fontSize: '18px' }}>
          Port List
        </h4>

        <div className="d-flex flex-column flex-sm-row flex-wrap align-items-start align-items-sm-center gap-2 ms-sm-auto">
          <button
            type="button"
            style={syncing ? { ...SYNC_BTN_STYLE, opacity: 0.7 } : SYNC_BTN_STYLE}
            onClick={handleSyncFromHardware}
            disabled={syncing}
            title="Pull live state from hardware and refresh status column"
          >
            {syncing ? (
              <><span className="spinner-border spinner-border-sm me-1" />Syncing…</>
            ) : (
              <>⚡ Sync from Hardware{lastSyncTime ? ` · ${lastSyncTime.toLocaleTimeString()}` : ''}</>
            )}
          </button>

          <button
            type="button"
            className={`btn btn-sm ${showArchived ? 'btn-warning' : 'btn-outline-warning'}`}
            onClick={() => setShowArchived(!showArchived)}
          >
            {showArchived ? 'Hide Archived' : 'Show Archived'}
          </button>

          {/* Search + filters */}
          <PortSearch
            searchTerm={searchTerm}
            handleSearchChange={handleSearchChange}
            clearSearch={clearSearch}
            showUnassigned={showUnassigned}
            sortByOldestMsg={sortByOldestMsg}
            setSortByOldestMsg={setSortByOldestMsg}
            lifecycleFilter={lifecycleFilter}
            lifecycleCounts={lifecycleCounts}
            toggleLifecycleFilter={toggleLifecycleFilter}
            clearLifecycleFilter={clearLifecycleFilter}
            handleExportCSV={handleExportCSV}
            gateways={gateways}
            selectedGatewayId={selectedGatewayId}
            handleGatewayFilterChange={handleGatewayFilterChange}
            clearGatewayFilter={clearGatewayFilter}
            selectedSourceFilter={selectedSourceFilter}
            handleSourceFilterChange={handleSourceFilterChange}
            clearSourceFilter={clearSourceFilter}
            selectedConversionFilter={selectedConversionFilter}
            handleConversionFilterChange={handleConversionFilterChange}
            clearConversionFilter={clearConversionFilter}
            NO_BOX_FILTER={NO_BOX_FILTER}
            selectedMobileOperator={selectedMobileOperator}
            mobileOperatorOptions={mobileOperatorOptions}
            UNKNOWN_OPERATOR={UNKNOWN_OPERATOR}
            handleMobileOperatorFilterChange={handleMobileOperatorFilterChange}
            clearMobileOperatorFilter={clearMobileOperatorFilter}
          />

          {/* Pagination summary + controls */}
          <div className="d-flex flex-wrap align-items-center gap-2">
            <div
              className="text-muted"
              style={{ fontSize: '12px', minWidth: '140px' }}
            >
              Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{' '}
              {Math.min(
                currentPage * ITEMS_PER_PAGE,
                sortedPorts.length
              )}{' '}
              of {sortedPorts.length} entries
            </div>

            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              setCurrentPage={setCurrentPage}
            />
          </div>
        </div>
      </div>

      <SimLifecycleBanner
        lifecycleCounts={lifecycleCounts}
        lifecycleFilter={lifecycleFilter}
        toggleLifecycleFilter={toggleLifecycleFilter}
      />

      {/* Table */}
      <PortTable
        ports={paginatedPorts}
        loading={loading}
        gateways={gateways}
        portStatus={portStatus}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        handleArchive={handleArchive}
        handleActivate={handleActivate}
        handleGetNumber={canGetNumber ? handleGetNumber : undefined}
        gettingNumberPortId={gettingNumberPortId}
        handleRequestPacCode={canGetNumber ? handleRequestPacCode : undefined}
        gettingPacCodePortId={gettingPacCodePortId}
        handleGetBalance={canGetNumber ? handleGetBalance : undefined}
        gettingBalancePortId={gettingBalancePortId}
        readOnly={readOnly}
      />
    </div>
  );
}

export default PortManagement;

